import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vocabularyQuestions, vocabTypeMeta } from '@/mocks/vocabularyQuestions';
import type { VocabType } from '@/mocks/vocabularyQuestions';
import { readingQuestions, readingTypeMeta } from '@/mocks/readingQuestions';
import type { ReadingType } from '@/mocks/readingQuestions';
import { vocabTypeColorMap, readingTypeColorMap } from './typeColors';
import { useTestResult } from '@/hooks/useTestResult';
import SaveStatusBanner from '@/components/feature/SaveStatusBanner';
import { literacyTopPercentile, getGradeInfo, topPercentileLabel } from '@/lib/percentile';
import type { LiteracyAnswers } from '@/pages/literacy-test/page';

interface Props {
  answers: LiteracyAnswers;
  onRetake: () => void;
}

function computeResult(answers: LiteracyAnswers) {
  let vocabCorrect = 0;
  let readingCorrect = 0;

  const vocabScores: Record<VocabType, { correct: number; total: number }> = {
    daily: { correct: 0, total: 0 },
    current: { correct: 0, total: 0 },
    economy: { correct: 0, total: 0 },
    science: { correct: 0, total: 0 },
    art: { correct: 0, total: 0 },
    politics: { correct: 0, total: 0 },
    culture: { correct: 0, total: 0 },
  };

  const readingScores: Record<ReadingType, { correct: number; total: number }> = {
    daily: { correct: 0, total: 0 },
    current: { correct: 0, total: 0 },
    economy: { correct: 0, total: 0 },
    science: { correct: 0, total: 0 },
    culture: { correct: 0, total: 0 },
    humanities: { correct: 0, total: 0 },
  };

  vocabularyQuestions.forEach((q) => {
    vocabScores[q.type].total += 1;
    if (answers.vocab[q.id] === q.answer) {
      vocabCorrect += 1;
      vocabScores[q.type].correct += 1;
    }
  });

  readingQuestions.forEach((q) => {
    readingScores[q.type].total += 1;
    if (answers.reading[q.id] === q.answer) {
      readingCorrect += 1;
      readingScores[q.type].correct += 1;
    }
  });

  const totalCorrect = vocabCorrect + readingCorrect;
  const totalQuestions = vocabularyQuestions.length + readingQuestions.length;
  const vocabPct = Math.round((vocabCorrect / vocabularyQuestions.length) * 100);
  const readingPct = Math.round((readingCorrect / readingQuestions.length) * 100);
  const totalPct = Math.round((totalCorrect / totalQuestions) * 100);

  return { vocabCorrect, readingCorrect, totalCorrect, totalQuestions, vocabPct, readingPct, totalPct, vocabScores, readingScores };
}

// 등급에 따른 강조 색상 (1~3 상위권 / 4~6 중위권 / 7~9 하위권)
function gradeTone(grade: number): { badge: string; text: string } {
  if (grade <= 3) return { badge: 'bg-primary-50 border-primary-200', text: 'text-primary-700' };
  if (grade <= 6) return { badge: 'bg-secondary-50 border-secondary-200', text: 'text-secondary-700' };
  return { badge: 'bg-accent-50 border-accent-200', text: 'text-accent-700' };
}

function renderOptions(options: string[], correctIdx: number, userIdx: number | undefined) {
  return (
    <div className="space-y-1.5">
      {options.map((opt, idx) => {
        const isCorrect = idx === correctIdx;
        const isUserWrong = userIdx !== undefined && idx === userIdx && idx !== correctIdx;
        return (
          <div
            key={idx}
            className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-sm ${
              isCorrect
                ? 'bg-primary-50 border-primary-300 text-primary-800'
                : isUserWrong
                ? 'bg-accent-50 border-accent-300 text-accent-800'
                : 'border-background-200 text-foreground-600'
            }`}
          >
            <i
              className={`${
                isCorrect
                  ? 'ri-checkbox-circle-fill text-primary-500'
                  : isUserWrong
                  ? 'ri-close-circle-fill text-accent-500'
                  : 'ri-circle-line text-foreground-300'
              } flex-shrink-0 mt-0.5`}
            ></i>
            <span className="leading-snug">
              {opt}
              {isCorrect && <span className="text-xs text-primary-500 ml-1">(정답)</span>}
              {isUserWrong && <span className="text-xs text-accent-500 ml-1">(내 답)</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function LiteracyResult({ answers, onRetake }: Props) {
  const navigate = useNavigate();
  const { saveTestResult, saveStatus } = useTestResult();
  const [reviewFilter, setReviewFilter] = useState<'wrong' | 'all'>('wrong');

  const result = computeResult(answers);
  const topPct = literacyTopPercentile(result.totalPct);
  const gradeInfo = getGradeInfo(topPct);
  const pctLabel = topPercentileLabel(topPct);
  const tone = gradeTone(gradeInfo.grade);

  const vocabReviewed = vocabularyQuestions
    .map((q) => ({ q, userAnswer: answers.vocab[q.id], isCorrect: answers.vocab[q.id] === q.answer }))
    .filter((item) => reviewFilter === 'all' || !item.isCorrect);
  const readingReviewed = readingQuestions
    .map((q) => ({ q, userAnswer: answers.reading[q.id], isCorrect: answers.reading[q.id] === q.answer }))
    .filter((item) => reviewFilter === 'all' || !item.isCorrect);

  const wrongVocabCount = vocabularyQuestions.filter((q) => answers.vocab[q.id] !== q.answer).length;
  const wrongReadingCount = readingQuestions.filter((q) => answers.reading[q.id] !== q.answer).length;
  const wrongTotal = wrongVocabCount + wrongReadingCount;

  useEffect(() => {
    saveTestResult('literacy', '어휘력·문해력 검사', result.totalCorrect, {
      vocabCorrect: result.vocabCorrect,
      readingCorrect: result.readingCorrect,
      totalCorrect: result.totalCorrect,
      totalQuestions: result.totalQuestions,
      vocabPct: result.vocabPct,
      readingPct: result.readingPct,
      totalPct: result.totalPct,
      percentile: topPct,
      grade: gradeInfo.grade,
      vocabScores: result.vocabScores,
      readingScores: result.readingScores,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Result Header */}
      <div className="bg-primary-500 rounded-2xl p-6 md:p-8 text-white text-center mb-6">
        <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-4">
          <i className="ri-trophy-line text-2xl text-white"></i>
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">
          검사 결과
        </h1>
        <p className="text-primary-100 text-sm">
          어휘력 · 문해력 종합 결과
        </p>
      </div>

      <SaveStatusBanner status={saveStatus} />

      {/* 종합 결과 */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 text-center mb-4">
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full border-2 ${tone.badge} mb-4`}>
          <span className={`font-heading text-3xl font-bold ${tone.text}`}>{gradeInfo.grade}</span>
        </div>
        <div className="text-2xl md:text-3xl font-bold text-foreground-950 mb-1">{gradeInfo.label}</div>
        <div className="text-foreground-500 text-sm mb-5">{gradeInfo.range} · {gradeInfo.description}</div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-background-100 border border-background-200 rounded-xl p-4">
            <div className="text-2xl font-bold text-foreground-950">{pctLabel.direction} {pctLabel.value}%</div>
            <div className="text-xs text-foreground-500 mt-1">전체 백분위</div>
          </div>
          <div className="bg-background-100 border border-background-200 rounded-xl p-4">
            <div className="text-2xl font-bold text-foreground-950">{result.totalCorrect}<span className="text-sm text-foreground-500"> / {result.totalQuestions}</span></div>
            <div className="text-xs text-foreground-500 mt-1">정답 문항</div>
          </div>
          <div className="bg-background-100 border border-background-200 rounded-xl p-4">
            <div className="text-2xl font-bold text-foreground-950">{result.totalPct}%</div>
            <div className="text-xs text-foreground-500 mt-1">종합 정답률</div>
          </div>
        </div>
      </div>

      {/* 어휘력 유형별 분석 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h2 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-font-size text-primary-500"></i>
          어휘력 유형별 분석
        </h2>
        <div className="space-y-4">
          {vocabTypeMeta.map((cat) => {
            const score = result.vocabScores[cat.id];
            const pct = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;
            return (
              <div key={cat.id}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <i className={`${cat.icon} text-foreground-500 text-sm`}></i>
                    <span className="text-sm text-foreground-700">{cat.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">
                    {score.correct}<span className="text-foreground-400 text-xs"> / {score.total}</span>
                  </span>
                </div>
                <div className="w-full bg-background-200 rounded-full h-2.5">
                  <div
                    className={`${vocabTypeColorMap[cat.id].bar} h-2.5 rounded-full transition-all duration-500`}
                    style={{ width: `${pct}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 문해력 유형별 분석 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <h2 className="font-semibold text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-file-text-line text-accent-500"></i>
          문해력 유형별 분석
        </h2>
        <div className="space-y-4">
          {readingTypeMeta.map((cat) => {
            const score = result.readingScores[cat.id];
            const pct = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;
            return (
              <div key={cat.id}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <i className={`${cat.icon} text-foreground-500 text-sm`}></i>
                    <span className="text-sm text-foreground-700">{cat.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900">
                    {score.correct}<span className="text-foreground-400 text-xs"> / {score.total}</span>
                  </span>
                </div>
                <div className="w-full bg-background-200 rounded-full h-2.5">
                  <div
                    className={`${readingTypeColorMap[cat.id].bar} h-2.5 rounded-full transition-all duration-500`}
                    style={{ width: `${pct}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 답안 확인 */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="font-semibold text-foreground-950 flex items-center gap-2">
              <i className="ri-error-warning-line text-accent-500"></i>
              답안 확인
              <span className="text-sm font-normal text-foreground-500">({wrongTotal}문항 오답)</span>
            </h2>
            <p className="text-xs text-foreground-500 mt-1">내가 고른 답과 정답, 풀이를 확인해보세요.</p>
          </div>
          <div className="inline-flex bg-background-200 rounded-full p-1 self-start">
            <button
              onClick={() => setReviewFilter('wrong')}
              className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer ${
                reviewFilter === 'wrong' ? 'bg-white text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              오답 {wrongTotal}
            </button>
            <button
              onClick={() => setReviewFilter('all')}
              className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer ${
                reviewFilter === 'all' ? 'bg-white text-foreground-950' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              전체 {result.totalQuestions}
            </button>
          </div>
        </div>

        {reviewFilter === 'wrong' && wrongTotal === 0 ? (
          <div className="bg-primary-50 border border-primary-200 rounded-lg px-4 py-3 text-center">
            <p className="text-sm text-primary-700 font-medium">
              <i className="ri-emotion-happy-line mr-1"></i>모든 문항을 맞혔어요!
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {vocabReviewed.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2 flex items-center gap-2">
                  <i className="ri-font-size text-primary-500"></i>
                  어휘력 <span className="text-xs font-normal text-foreground-400">({vocabReviewed.length}문항)</span>
                </h3>
                <div className="space-y-3">
                  {vocabReviewed.map(({ q, userAnswer, isCorrect }) => (
                    <div key={q.id} className="border border-background-200 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className="font-bold text-foreground-950">‘{q.word}’</span>
                        {userAnswer === undefined ? (
                          <span className="whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-background-200 text-foreground-500">미응답</span>
                        ) : isCorrect ? (
                          <span className="whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">정답</span>
                        ) : (
                          <span className="whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">오답</span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-500 mb-3">{q.example}</p>
                      {renderOptions(q.options, q.answer, userAnswer)}
                      <div className="mt-3 bg-background-100 border border-background-200 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-foreground-700 leading-relaxed">
                          <i className="ri-lightbulb-flash-line text-accent-500 mr-1"></i>
                          <strong>풀이</strong> {q.explanation}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {readingReviewed.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-2 flex items-center gap-2">
                  <i className="ri-file-text-line text-accent-500"></i>
                  문해력 <span className="text-xs font-normal text-foreground-400">({readingReviewed.length}문항)</span>
                </h3>
                <div className="space-y-3">
                  {readingReviewed.map(({ q, userAnswer, isCorrect }) => (
                    <div key={q.id} className="border border-background-200 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className="text-xs font-semibold text-foreground-500">
                          {readingTypeMeta.find((c) => c.id === q.type)?.label}
                        </span>
                        {userAnswer === undefined ? (
                          <span className="whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-background-200 text-foreground-500">미응답</span>
                        ) : isCorrect ? (
                          <span className="whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">정답</span>
                        ) : (
                          <span className="whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">오답</span>
                        )}
                      </div>
                      <div className="bg-background-100 border border-background-200 rounded-lg px-3 py-2 mb-3">
                        <p className="text-xs text-foreground-600 leading-relaxed whitespace-pre-line">{q.passage}</p>
                      </div>
                      <p className="text-sm font-semibold text-foreground-950 mb-3">{q.question}</p>
                      {renderOptions(q.options, q.answer, userAnswer)}
                      <div className="mt-3 bg-background-100 border border-background-200 rounded-lg px-3 py-2.5">
                        <p className="text-xs text-foreground-700 leading-relaxed">
                          <i className="ri-lightbulb-flash-line text-accent-500 mr-1"></i>
                          <strong>풀이</strong> {q.explanation}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 해석 기준 */}
      <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 mb-6">
        <div className="flex gap-2">
          <i className="ri-information-line text-accent-500 flex-shrink-0 mt-0.5"></i>
          <p className="text-accent-700 text-sm leading-relaxed">
            <strong>해석 기준:</strong> 정답률을 정규분포(평균 60%·표준편차 15%)에 적용해 산출한 상위 백분위를 기준으로
            9등급을 부여합니다. 1등급 상위 4% · 2등급 4~11% · 3등급 11~23% · 4등급 23~40% · 5등급 40~60% ·
            6등급 60~77% · 7등급 77~89% · 8등급 89~96% · 9등급 96~100%. 이 검사는 참고용이며 정식 언어능력 평가를 대체하지 않습니다.
          </p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-primary-800 mb-3">
          <i className="ri-arrow-right-circle-line mr-2"></i>다음 단계
        </h3>
        <div className="space-y-2">
          <button
            onClick={() => navigate('/iq-test')}
            className="whitespace-nowrap w-full text-left text-sm text-primary-700 bg-white border border-primary-200 rounded-lg px-4 py-2.5 hover:bg-primary-100 cursor-pointer flex items-center gap-2"
          >
            <i className="ri-psychotherapy-line"></i>
            종합 인지능력(IQ) 검사하기
          </button>
          <button
            onClick={() => navigate('/career-test')}
            className="whitespace-nowrap w-full text-left text-sm text-primary-700 bg-white border border-primary-200 rounded-lg px-4 py-2.5 hover:bg-primary-100 cursor-pointer flex items-center gap-2"
          >
            <i className="ri-compass-3-line"></i>
            진로·계열 탐색 검사하기
          </button>
        </div>
      </div>

      {/* Retake Button */}
      <button
        onClick={onRetake}
        className="whitespace-nowrap w-full py-3.5 rounded-xl border border-background-300 text-foreground-600 font-semibold text-sm hover:bg-background-100 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line mr-2"></i>다시 검사하기
      </button>
    </div>
  );
}